<template>
    <div v-if="portalContentAndDesignStore.portalInitialized" class="flex-none w-96 h-screen bg-white border-r relative shadow-xl overflow-scroll">
        <div class="flex flex-col h-full">
            <!-- Top navigation -->
            <div class="pt-2 bg-gray-50"></div>
            <div class="flex justify-between bg-gray-50 px-6 sticky top-0 z-40 border-b">
                <div class="flex">
                  <tabs>
                    <tab
                        @click="setActiveLiveDesignerTab('layout')"
                        :active="activeLiveDesignerTab === 'layout'"
                        padding="py-3 px-5"
                        inactive-class="bg-gray-50 border-b"
                    >
                      <icon name="brush"></icon>
                    </tab>
                    <tab
                        v-if="showPageContentTab"
                        @click="setActiveLiveDesignerTab('page-content')"
                        :active="activeLiveDesignerTab === 'page-content'"
                        padding="py-3 px-5"
                        inactive-class="bg-gray-50 border-b"
                    >
                      <icon name="pencil-square"></icon>
                    </tab>
                    <tab
                        @click="setActiveLiveDesignerTab('translations')"
                        :active="activeLiveDesignerTab === 'translations'"
                        padding="py-3 px-5"
                        inactive-class="bg-gray-50 border-b"
                    >
                      <icon name="language"></icon>
                    </tab>
                  </tabs>
                </div>
                <btn :href="closeLiveDesignerRoute" class="bg-gray-50" icon-name="close" type="link" size="small" @click="resetDesignerState"></btn>
            </div>
            <!-- /Top navigation -->
            <div class="p-6 space-y-4 text-sm text-gray-500">
                <template v-if="isRoute('partners.program')">
                    <p>Modify the content of your landing page while in live mode.</p>
                    <p>After making changes, don't forget to hit
                        <span class="font-semibold">Save</span> to make these changes permanent.
                    </p>
                </template>
                <template v-else>
                    <template v-if="activeLiveDesignerTab === 'translations'">
                        <p>Manage your portal translations here.</p>
                        <translation-prefill-select-confirm />
                        <p>
                            Don't forget to hit
                            <span class="font-semibold">Save</span> to make these changes permanent.
                        </p>
                    </template>
                    <template v-else>
                        <p>Modify the design of your partner portal directly in live mode.</p>
                        <p>
                            Explore different styles and adjustments, and be sure to click
                          <span class="font-semibold">Save</span> to apply your changes permanently.
                        </p>
                    </template>
                </template>
            </div>
            <template v-if="activeLiveDesignerTab === 'layout'">
              <div id="accordion-collapse-layout" class="w-full text-gray-900">
                  <live-designer-accordion title="Branding">
                      <template #content>
                          <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                              <brand-settings :auth-view="authView" />
                          </div>
                      </template>
                  </live-designer-accordion>

                  <template v-if="showPublicSettings">
                      <live-designer-accordion title="Page">
                          <template #content>
                              <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <public-default-settings />
                              </div>
                          </template>
                      </live-designer-accordion>

                      <live-designer-accordion title="Content">
                          <template #content>
                              <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <public-content-settings />
                              </div>
                          </template>
                      </live-designer-accordion>

                      <live-designer-accordion
                          v-if="isRoute('partners.program') && portalContentAndDesignStore.landingPageSettings?.landing_page_resources?.general"
                          title="Settings"
                      >
                          <template #content>
                              <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <landing-page-settings />
                              </div>
                          </template>
                      </live-designer-accordion>
                      <live-designer-accordion v-if="isRoute('partners.leads.show')" title="Settings">
                          <template #content>
                              <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <leads-page-settings />
                              </div>
                          </template>
                      </live-designer-accordion>
                  </template>

                  <template v-else>
                      <live-designer-accordion title="Page">
                          <template #content>
                              <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <portal-default-settings :auth-view="authView" />
                              </div>
                          </template>
                      </live-designer-accordion>
                      <live-designer-accordion title="Layout">
                          <template #content>
                              <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <portal-layout-settings :auth-view="authView" />
                              </div>
                          </template>
                      </live-designer-accordion>
                      <live-designer-accordion title="Navigation">
                          <template #content>
                              <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <portal-navigation-settings :auth-view="authView" />
                              </div>
                          </template>
                      </live-designer-accordion>
                  </template>

                  <live-designer-accordion
                      v-if="isRoute('partners.login.show')"
                      title="Page structure"
                  >
                      <template #content>
                          <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                              <public-login-settings />
                          </div>
                      </template>
                  </live-designer-accordion>

                  <live-designer-accordion
                      v-if="isRoute('partners.register.show') && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance"
                      title="Page structure"
                  >
                      <template #content>
                          <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                              <public-signup-settings />
                          </div>
                      </template>
                  </live-designer-accordion>

                <live-designer-accordion v-if="isRoute('partners.login.show')" title="Settings">
                  <template #content>
                    <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                      <login-page-settings />
                    </div>
                  </template>
                </live-designer-accordion>

                  <live-designer-accordion v-if="isRoute('partners.register.show')" title="Settings">
                      <template #content>
                          <div class="p-5 border-t border-gray-200 grid grid-cols-1 gap-4">
                              <signup-page-settings />
                          </div>
                      </template>
                  </live-designer-accordion>
              </div>
            </template>
            <template v-if="activeLiveDesignerTab === 'page-content'">
              <div id="accordion-collapse-page-content" class="w-full text-gray-900">
                  <live-designer-accordion
                      v-if="isRoute('partners.program')"
                      title="Page resources"
                  >
                    <template #content>
                      <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <base-resources-builder
                            name="landingPageResources"
                            :program-type="portalContentAndDesignStore.program.program_type.type"
                            :resources="portalContentAndDesignStore.landingPageSettings.landing_page_resources?.resources || []"
                            :file-upload-url="portalContentAndDesignStore.getFileResourcesUploadRoute"
                            :file-delete-url="portalContentAndDesignStore.getFileResourcesDeleteRoute"
                            zero-fields-title="Start building your page"
                            zero-fields-message="Start building your custom landing page by adding resources."
                            options-container-class=""
                        />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="isRoute('partners.register.show')"
                      title="Custom fields"
                  >
                    <template #content>
                      <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <fields-builder
                            name="signup_fields"
                            v-model="portalContentAndDesignStore.signupPageSettings.signup_form.fields"
                            zero-fields-message="Add few fields for new customer signup form."
                            form-type="signup"
                        />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="isRoute('partners.leads.show')"
                      title="Custom fields"
                  >
                    <template #content>
                      <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <fields-builder
                            name="leads_fields"
                            v-model="portalContentAndDesignStore.leadsPageSettings.leads_form.fields"
                            zero-fields-message="Add few fields for new customer signup form."
                            form-type="signup"
                        />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="authView && vueRouterRoute.name === 'approval'"
                      title="Custom fields"
                  >
                    <template #content>
                      <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <fields-builder
                            name="approval_fields"
                            v-model="portalContentAndDesignStore.approvalPageSettings.approval_form.fields"
                            zero-fields-message="Add few fields for new customer approval."
                            form-type="approval"
                        />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="authView && vueRouterRoute.name === 'dashboard' && portalContentAndDesignStore.dashboardPageSettings.dashboard_resources"
                      title="Dashboard resources"
                  >
                    <template #content>
                      <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                          <dashboard-fields-resource-builder
                              v-model:resources="portalContentAndDesignStore.dashboardPageSettings.dashboard_resources"
                              :file_upload_url="portalContentAndDesignStore.getDashboardFileUploadRoute"
                              :file_delete_url="portalContentAndDesignStore.getDashboardFileDeleteRoute"
                              zero-fields-title="Custom dashboard resources live here"
                              zero-fields-message="Here you can create your own dashboard resource page that will be visible to your partners.
                 You can share links, create text or content blocks and anything else your partners might need."
                          />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="authView && vueRouterRoute.name === 'resources' && portalContentAndDesignStore.resourcesPageSettings.resources"
                      title="Resource page resources"
                  >
                    <template #content>
                      <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                          <resources-builder
                              :value="portalContentAndDesignStore.resourcesPageSettings.resources"
                              @update:value="portalContentAndDesignStore.resourcesPageSettings.resources = $event"
                              :file_upload_url="portalContentAndDesignStore.getResourcePageFileUploadRoute"
                              :file_delete_url="portalContentAndDesignStore.getResourcePageFileDeleteRoute"
                              zero-fields-title="Resources live here"
                              zero-fields-message="Here you can create your own resource page that will be visible to your partners.
                 You can share links, create text or content blocks and anything else your partners might need."
                              :show-update-button="false"
                              is-live-designer-view
                          />
                      </div>
                    </template>
                  </live-designer-accordion>

              </div>
            </template>
            <template v-if="activeLiveDesignerTab === 'translations'">
                <live-designer-accordion v-for="(accordionData, index) in portalContentAndDesignStore.translationSettingsConfig" :key="`translations-${index}`"
                    :title="accordionData.label"
                >
                    <template #content>
                        <div class="p-6 border-t border-gray-200 grid grid-cols-1 gap-4">
                            <translation-settings :translations="accordionData.translations" />
                        </div>
                    </template>
                </live-designer-accordion>
            </template>
            <!-- Footer -->
            <div class="p-4 bg-gray-50 sticky bottom-0 z-40 border-t mt-auto">
                <div class="mb-4">
                    <form-errors :store-form-errors="liveDesignerErrors" />
                    <alert v-if="liveDesignerSuccessMessage"
                           type="success"
                    >
                        {{ liveDesignerSuccessMessage }}
                    </alert>
                </div>
                <div class="flex gap-4">
                    <btn type="secondary" size="large" class="w-full mb-4 text-black" @click="resetLiveDesigner">
                        Revert recent changes
                    </btn>
                </div>
                <btn
                    type="primary"
                    size="large"
                    class="w-full"
                    @click="saveLiveDesigner"
                    :loading="savingLiveDesignerSettings"
                    :disabled="savingLiveDesignerSettings"
                >
                    Save
                </btn>
            </div>
            <!-- /Footer -->
        </div>
    </div>
    <div v-if="shouldShowLiveDesignerPageSwitcher" class="absolute top-4 right-4 z-[99999999]">
        <div class="bg-white rounded p-2 shadow-xl flex items-center gap-2">
            <div class="text-sm text-black font-semibold">
                <icon name="pages"></icon>
            </div>
            <a class="text-sm text-black hover:opacity-75"
               :class="isActive('partners.program')"
               href="/program"
            >
                Program
            </a>
            <a class="text-sm text-black hover:opacity-75"
               :class="isActive('partners.login.show')"
               href="/login"
            >
                Log-in
            </a>
            <a class="text-sm text-black hover:opacity-75"
               :class="isActive('partners.register.show')"
               href="/register"
            >
                Sign-up
            </a>
            <a class="text-sm text-black hover:opacity-75"
               :class="isActive('partners.leads.show')"
               href="/submit"
            >
                Leads
            </a>
        </div>
    </div>
</template>

<script setup>
import {defineProps, reactive, ref} from 'vue';
import LiveDesignerAccordion from "./includes/Accordion.vue";
import BrandSettings from "./includes/BrandSettings.vue";
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import {computed} from "vue";
import PublicDefaultSettings from "./includes/PublicDefaultSettings.vue";
import PublicContentSettings from "./includes/PublicContentSettings.vue";
import PortalDefaultSettings from "./includes/PortalDefaultSettings.vue";
import PortalLayoutSettings from "./includes/PortalLayoutSettings.vue";
import PortalNavigationSettings from "./includes/PortalNavigationSettings.vue";
import PublicLoginSettings from "./includes/PublicLoginSettings.vue";
import PublicSignupSettings from "./includes/PublicSignupSettings.vue";
import Tabs from "../../../elements/tabs/Tabs.vue";
import Tab from "../../../elements/tabs/Tab.vue";
import {isRoute} from "../../../helpers/common";
import FieldsBuilder from "../../app/fields-builder/FieldsBuilder.vue";
import Alert from "../../../elements/Alert.vue";
import FormErrors from "../Pub/Pages/Errors/FormErrors.vue";
import Btn from "../../../elements/Btn.vue";
import {useRoute} from "vue-router";
import DashboardFieldsResourceBuilder from "../../app/preferences/pages/dashboard/DashboardFieldsResourceBuilder.vue";
import ResourcesBuilder from "../../app/resources/ResourcesBuilder.vue";
import TranslationSettings from "./includes/TranslationSettings.vue";
import LandingPageSettings from "./includes/LandingPageSettings.vue";
import SignupPageSettings from "./includes/SignupPageSettings.vue";
import LeadsPageSettings from "./includes/LeadsPageSettings.vue";
import TranslationPrefillSelectConfirm from "./includes/TranslationPrefillSelectConfirm.vue";
import LoginPageSettings from "./includes/LoginPageSettings.vue";

const props = defineProps({
    authView: {
        type: Boolean,
        default: false
    }
});

const vueRouterRoute = props.authView ? useRoute() : null;

const actionType = ref('');

const portalContentAndDesignStore =  usePortalContentAndDesignStore()

const liveDesignerErrors = reactive({
            responseError: null,
            validationErrors: null
        });

const liveDesignerSuccessMessage = ref(null);
const savingLiveDesignerSettings = ref(false);

const shouldShowLiveDesignerPageSwitcher = computed(() => {
    return [
        "partners.program",
        "partners.login.show",
        "partners.register.show",
        "partners.password.request.show",
        "partners.leads.show",
    ].includes(route().current());
});

const isActive = (routeName) => {
    return route().current() === routeName ? 'font-semibold' : '';
};

const saveLiveDesigner = async () => {
    actionType.value = 'save';
    await sendRequest();
};

const resetLiveDesigner = async () => {
    portalContentAndDesignStore.resetCurrentPageToInitialState()
};

const routeName = computed(() => route().current());

const showPublicSettings = computed(() => {
    return [
        "partners.login.show",
        "partners.register.show",
        "partners.password.request.show",
        "partners.program",
        "partners.leads.show",
    ].includes(routeName.value);
});

const showPageContentTab = computed(() => {

    if (portalContentAndDesignStore.isAuthView)
        return vueRouterRoute.name === 'dashboard'
            || vueRouterRoute.name === 'resources'
            || vueRouterRoute.name === 'approval';
    else {
        return isRoute('partners.program')
         || isRoute('partners.register.show')
         || isRoute('partners.leads.show')
    }

});

const sendRequest = async () => {

    savingLiveDesignerSettings.value = true;

    resetLiveDesignerErrorsAndMessages()

    let payload = {
        programId: portalContentAndDesignStore.program.id,
        actionType: actionType.value,
        ...portalContentAndDesignStore.translationSettings,
        ...portalContentAndDesignStore.portalLayoutSettings,
    }

    if (portalContentAndDesignStore.isAuthView){
        if (vueRouterRoute.name === 'dashboard') {
            payload = {
                ...payload,
                dashboard_page_resources: JSON.stringify(portalContentAndDesignStore.dashboardPageSettings.dashboard_resources || []),
            }
        }

        if (vueRouterRoute.name === 'resources') {
            payload = {
                ...payload,
                resource_page_resources: JSON.stringify(portalContentAndDesignStore.resourcesPageSettings.resources || []),
            }
        }

        if (vueRouterRoute.name === 'approval') {
            payload = {
                ...payload,
                approval_fields: JSON.stringify(portalContentAndDesignStore.approvalPageSettings.approval_form?.fields || []),
                approval_fields_title: portalContentAndDesignStore.approvalPageSettings.approval?.title,
                approval_fields_description: portalContentAndDesignStore.approvalPageSettings.approval?.description,
            }
        }
    }


    if (isRoute('partners.login.show')) {
        payload = {
            ...payload,
            ...portalContentAndDesignStore.translationSettings,
            program_login_page_branding_type: portalContentAndDesignStore.loginPageSettings?.program_login_page_branding_type || 'none',
        }
    }

    if (isRoute('partners.program')) {
        payload = {
            ...payload,
            ...portalContentAndDesignStore.landingPageSettings.translations,
            landing_page_resources: JSON.stringify(portalContentAndDesignStore.landingPageSettings.landing_page_resources || []),
        }
    }

    if (isRoute('partners.register.show')) {
        payload = {
            ...payload,
            ...portalContentAndDesignStore.translationSettings,
            portal_signup_tos_text: portalContentAndDesignStore.signupPageSettings.signup?.portal_signup_tos_text || '',
            portal_signup_tos_url: portalContentAndDesignStore.signupPageSettings.signup?.portal_signup_tos_url || '',
            portal_signup_tos_saved_locally: portalContentAndDesignStore.signupPageSettings.signup?.portal_signup_tos_saved_locally || false,
            portal_signup_marketing_consent_text: portalContentAndDesignStore.signupPageSettings.signup?.marketing_consent || '',
            program_signup_page_branding_type: portalContentAndDesignStore.signupPageSettings.signup?.program_signup_page_branding_type || 'none',
            signup_fields: JSON.stringify(portalContentAndDesignStore.signupPageSettings.signup_form.fields || []),
        }
    }

    if (isRoute('partners.leads.show')) {

        const program_leads_additional_section = portalContentAndDesignStore.leadsPageSettings.program_leads_include_additional_section || false;

        payload = {
            ...payload,
            ...portalContentAndDesignStore.translationSettings,
            leads_form_fields: JSON.stringify(portalContentAndDesignStore.leadsPageSettings.leads_form.fields || []),
            program_leads_page_branding_type: portalContentAndDesignStore.leadsPageSettings.program_leads_page_branding_type || 'none',
            program_leads_include_signup_links: portalContentAndDesignStore.leadsPageSettings.program_leads_include_signup_links || false,
            program_leads_include_additional_section: program_leads_additional_section,
        }

        if (program_leads_additional_section) {
            payload = {
                ...payload,
                program_leads_page_additional_description: portalContentAndDesignStore.leadsPageSettings.program_leads_page_additional_description || '',
            }
        }
    }

    if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
        payload = {
            ...payload,
            ...portalContentAndDesignStore.translationSettings,
        }
    }

    axios.post(route('partners_int.live-designer.update'), payload)
        .then(response => {
            if (+response.data.status === 1) {
                resetLiveDesignerErrorsAndMessages()
                liveDesignerSuccessMessage.value = response.data.message;
            } else {
                liveDesignerErrors.responseError = response.data.error;
            }
        })
        .catch(error => {
            if (error.response && error.response.status === 422) {
                liveDesignerErrors.validationErrors = error.response.data.errors;
            } else {
                console.log(error)
                liveDesignerErrors.responseError = error.response?.data?.error || 'An error occurred. See console for details';
            }
        }).finally(() => {
            savingLiveDesignerSettings.value = false;
        });
}

const resetLiveDesignerErrorsAndMessages = () => {
    liveDesignerErrors.responseError = null;
    liveDesignerErrors.validationErrors = null;
    liveDesignerSuccessMessage.value = null;
}

const closeLiveDesignerRoute = route('partners.live-designer.close');

const resetDesignerState = () => {
    document.cookie = 'designer_state=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Domain=' + location.hostname;
};

const activeLiveDesignerTab = ref('layout');

const setActiveLiveDesignerTab = (tab) => {
  activeLiveDesignerTab.value = tab;
}
</script>
